/** Keyboard ripple effect — visual flash feedback on option cards when triggered by keyboard shortcuts. */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { motion } from 'motion/react';

// ─── Context ────────────────────────────────────────────────────────────────

interface KeyboardRippleContextValue {
  rippleIndex: number | null;
  rippleKey: number;
  triggerRipple: (index: number) => void;
}

const KeyboardRippleContext = createContext<KeyboardRippleContextValue>({
  rippleIndex: null,
  rippleKey: 0,
  triggerRipple: () => {},
});

// ─── Provider ───────────────────────────────────────────────────────────────

export function KeyboardRippleProvider({ children }: { children: ReactNode }) {
  const [rippleIndex, setRippleIndex] = useState<number | null>(null);
  const [rippleKey, setRippleKey] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const triggerRipple = useCallback((index: number) => {
    clearTimeout(timeoutRef.current);
    setRippleIndex(index);
    setRippleKey((k) => k + 1);
    timeoutRef.current = setTimeout(() => setRippleIndex(null), 400);
  }, []);

  const value = useMemo(
    () => ({ rippleIndex, rippleKey, triggerRipple }),
    [rippleIndex, rippleKey, triggerRipple]
  );

  return <KeyboardRippleContext value={value}>{children}</KeyboardRippleContext>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useKeyboardRipple() {
  return useContext(KeyboardRippleContext);
}

// ─── Flash component ────────────────────────────────────────────────────────

/** Brief flash overlay for keyboard-triggered option selection. */
export function RippleFlash({ active, rippleKey }: { active: boolean; rippleKey: number }) {
  if (!active) return null;
  return (
    <motion.div
      key={rippleKey}
      initial={{ opacity: 1 }}
      animate={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        background: 'rgba(155, 107, 218, 0.18)',
        pointerEvents: 'none',
      }}
    />
  );
}
